/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Cinematic AI Video Generation Studio State Management Store (Task 10.4.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';
import { useWalletStore } from './useWalletStore';

export type VideoGenerationMode = 'text-to-video' | 'image-to-video';
export type VideoModelId = 'luma-dream-machine' | 'kling-v1' | 'runway-gen3';
export type VideoDuration = 5 | 10;
export type VideoAspectRatio = '16:9' | '9:16' | '1:1';
export type CameraDirection =
  | 'static'
  | 'pan-left'
  | 'pan-right'
  | 'tilt-up'
  | 'tilt-down'
  | 'zoom-in'
  | 'zoom-out'
  | 'orbit';

export interface GeneratedVideoClip {
  id: string;
  videoUrl: string;
  thumbnailUrl: string;
  prompt: string;
  negativePrompt?: string;
  keyframeUrl?: string | null;
  model: VideoModelId;
  duration: VideoDuration;
  aspectRatio: VideoAspectRatio;
  cameraDirection: CameraDirection;
  motionIntensity: number;
  loop: boolean;
  createdAt: string;
}

export interface VideoModelInfo {
  id: VideoModelId;
  titleKey: string;
  descKey: string;
  badge: string;
  isPopular?: boolean;
}

export interface SampleKeyframe {
  id: string;
  url: string;
  title: string;
}

export interface SamplePromptPreset {
  id: string;
  title: string;
  prompt: string;
  camera: CameraDirection;
  intensity: number;
}

export interface VideoStudioState {
  // Input parameters
  mode: VideoGenerationMode;
  prompt: string;
  negativePrompt: string;
  keyframeUrl: string | null;
  model: VideoModelId;
  duration: VideoDuration;
  aspectRatio: VideoAspectRatio;
  cameraDirection: CameraDirection;
  motionIntensity: number; // 1 to 10
  loop: boolean;

  // Execution state
  isGenerating: boolean;
  isEnhancingPrompt: boolean;
  generationProgress: number; // 0 to 100
  generationStage: string;
  errorMessage: string | null;

  // Active item & gallery
  activeVideo: GeneratedVideoClip | null;
  generatedVideos: GeneratedVideoClip[];

  // Actions
  setMode: (mode: VideoGenerationMode) => void;
  setPrompt: (prompt: string) => void;
  setNegativePrompt: (negativePrompt: string) => void;
  setKeyframeUrl: (url: string | null) => void;
  setModel: (model: VideoModelId) => void;
  setDuration: (duration: VideoDuration) => void;
  setAspectRatio: (aspectRatio: VideoAspectRatio) => void;
  setCameraDirection: (dir: CameraDirection) => void;
  setMotionIntensity: (intensity: number) => void;
  setLoop: (loop: boolean) => void;
  setActiveVideo: (video: GeneratedVideoClip | null) => void;
  applyPreset: (preset: SamplePromptPreset) => void;

  enhancePromptWithAI: () => Promise<void>;
  generateVideo: () => Promise<boolean>;
  deleteVideo: (id: string) => void;
  resetStudio: () => void;
}

export const VIDEO_5S_COST = 10;
export const VIDEO_10S_COST = 20;

export const VIDEO_MODELS: VideoModelInfo[] = [
  {
    id: 'luma-dream-machine',
    titleKey: 'video.modelLumaTitle',
    descKey: 'video.modelLumaDesc',
    badge: 'Ultra Fast',
    isPopular: true,
  },
  {
    id: 'kling-v1',
    titleKey: 'video.modelKlingTitle',
    descKey: 'video.modelKlingDesc',
    badge: 'High Fidelity',
  },
  {
    id: 'runway-gen3',
    titleKey: 'video.modelRunwayTitle',
    descKey: 'video.modelRunwayDesc',
    badge: 'Cinematic Alpha',
  },
];

export const SAMPLE_KEYFRAMES: SampleKeyframe[] = [
  {
    id: 'kf-cyberpunk',
    url: 'https://images.unsplash.com/photo-1508739773434-c26b3d09e071?w=800&auto=format&fit=crop&q=80',
    title: 'Neon Cyberpunk Alley',
  },
  {
    id: 'kf-cosmos',
    url: 'https://images.unsplash.com/photo-1506703719100-a0f3a48c0f86?w=800&auto=format&fit=crop&q=80',
    title: 'Starlight Nebula Cloud',
  },
  {
    id: 'kf-waterfall',
    url: 'https://images.unsplash.com/photo-1448375240586-882707db888b?w=800&auto=format&fit=crop&q=80',
    title: 'Misty Ancient Woodland',
  },
  {
    id: 'kf-fantasy',
    url: 'https://images.unsplash.com/photo-1534447677768-be436bb09401?w=800&auto=format&fit=crop&q=80',
    title: 'Ethereal Explorer',
  },
];

export const SAMPLE_PROMPT_PRESETS: SamplePromptPreset[] = [
  {
    id: 'preset-cyber-flight',
    title: 'Neon Metropolis Flythrough',
    prompt:
      'Cinematic aerial drone shot soaring through a dense neon-lit cyberpunk metropolis in heavy rain, holographic billboards reflecting on wet glass skyscrapers, volumetric smog, anamorphic lens flare, 8k resolution',
    camera: 'zoom-in',
    intensity: 6,
  },
  {
    id: 'preset-cosmic-whale',
    title: 'Celestial Space Leviathan',
    prompt:
      'A majestic luminescent cosmic whale gliding gracefully across a glittering star nursery and swirling violet nebulas, starlight dust trails, ethereal slow motion, hyper-detailed photorealism',
    camera: 'orbit',
    intensity: 4,
  },
  {
    id: 'preset-ancient-temple',
    title: 'Sacred Forest Shrine',
    prompt:
      'Slow sweeping cinematic push towards a moss-covered ancient stone temple buried inside misty bamboo forest, golden morning sunbeams breaking through canopy, floating spore dust',
    camera: 'pan-right',
    intensity: 5,
  },
  {
    id: 'preset-ocean-sunset',
    title: 'Golden Sunset Barrel Wave',
    prompt:
      'Dramatic slow-motion tracking shot inside a crystalline turquoise barrel wave breaking against warm golden hour sunset, sparkling water droplets catching the sun, hyper-realistic fluid physics',
    camera: 'pan-left',
    intensity: 7,
  },
];

const INITIAL_VIDEO_GALLERY: GeneratedVideoClip[] = [
  {
    id: 'vid-demo-01',
    videoUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
    thumbnailUrl: 'https://images.unsplash.com/photo-1508739773434-c26b3d09e071?w=800&auto=format&fit=crop&q=80',
    prompt:
      'Cinematic drone shot flying through a rain-soaked cyberpunk metropolis with reflective neon puddles, volumetric mist, photorealistic 8k',
    model: 'luma-dream-machine',
    duration: 5,
    aspectRatio: '16:9',
    cameraDirection: 'zoom-in',
    motionIntensity: 6,
    loop: false,
    createdAt: '2026-10-25T06:30:00Z',
  },
  {
    id: 'vid-demo-02',
    videoUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4',
    thumbnailUrl: 'https://images.unsplash.com/photo-1506703719100-a0f3a48c0f86?w=800&auto=format&fit=crop&q=80',
    prompt:
      'A majestic celestial whale swimming gracefully across iridescent nebula clouds and distant star clusters, hyper-detailed cosmic lighting',
    model: 'kling-v1',
    duration: 10,
    aspectRatio: '16:9',
    cameraDirection: 'orbit',
    motionIntensity: 4,
    loop: true,
    createdAt: '2026-10-25T06:15:00Z',
  },
];

export const useVideoStudioStore = create<VideoStudioState>((set, get) => ({
  mode: 'text-to-video',
  prompt:
    'A hyper-realistic cinematic aerial shot gliding through ancient neon-lit cyber temple in rain, cinematic lighting, volumetric mist, photorealistic 8k',
  negativePrompt: 'blurry, low quality, static, deformed, flickering, watermark',
  keyframeUrl: null,
  model: 'luma-dream-machine',
  duration: 5,
  aspectRatio: '16:9',
  cameraDirection: 'zoom-in',
  motionIntensity: 5,
  loop: false,

  isGenerating: false,
  isEnhancingPrompt: false,
  generationProgress: 0,
  generationStage: '',
  errorMessage: null,

  activeVideo: INITIAL_VIDEO_GALLERY[0],
  generatedVideos: INITIAL_VIDEO_GALLERY,

  setMode: (mode) => set({ mode }),
  setPrompt: (prompt) => set({ prompt }),
  setNegativePrompt: (negativePrompt) => set({ negativePrompt }),
  setKeyframeUrl: (url) => {
    set({
      keyframeUrl: url,
      mode: url ? 'image-to-video' : get().mode,
    });
  },
  setModel: (model) => set({ model }),
  setDuration: (duration) => set({ duration }),
  setAspectRatio: (aspectRatio) => set({ aspectRatio }),
  setCameraDirection: (cameraDirection) => set({ cameraDirection }),
  setMotionIntensity: (intensity) =>
    set({ motionIntensity: Math.max(1, Math.min(10, intensity)) }),
  setLoop: (loop) => set({ loop }),
  setActiveVideo: (video) => set({ activeVideo: video }),

  applyPreset: (preset) => {
    set({
      prompt: preset.prompt,
      cameraDirection: preset.camera,
      motionIntensity: preset.intensity,
    });
  },

  enhancePromptWithAI: async () => {
    const { prompt, cameraDirection } = get();
    if (!prompt.trim()) return;

    set({ isEnhancingPrompt: true, errorMessage: null });

    try {
      // Simulate prompt optimization delay
      await new Promise((resolve) => setTimeout(resolve, 800));

      const cameraToken =
        cameraDirection !== 'static'
          ? `[Camera: Dynamic ${cameraDirection.replace('-', ' ')} movement], `
          : '';

      const enhanced = `${prompt.trim()}, ${cameraToken}photorealistic 8k UHD, mastered cinematic color grading, 35mm anamorphic lens, atmospheric volumetric haze, fluid realistic physics simulation, award-winning cinematography`;

      set({ prompt: enhanced, isEnhancingPrompt: false });
    } catch {
      set({ isEnhancingPrompt: false });
    }
  },

  generateVideo: async () => {
    const {
      prompt,
      negativePrompt,
      keyframeUrl,
      model,
      duration,
      aspectRatio,
      cameraDirection,
      motionIntensity,
      loop,
    } = get();

    if (!prompt.trim()) {
      set({ errorMessage: 'Please provide a scene description prompt.' });
      return false;
    }

    const requiredCredits = duration === 10 ? VIDEO_10S_COST : VIDEO_5S_COST;
    const walletStore = useWalletStore.getState();

    // Guard against insufficient credits
    if (walletStore.balance < requiredCredits) {
      set({
        errorMessage: 'Insufficient credits in wallet. Please top up your balance.',
      });
      return false;
    }

    // Optimistic deduction
    walletStore.optimisticDeduct(requiredCredits);

    set({
      isGenerating: true,
      generationProgress: 10,
      generationStage: 'Job queued in Edge Workers queue, processing...',
      errorMessage: null,
    });

    try {
      // Call edge video generation API if available, or simulate realistic progress stages
      const payload = {
        prompt,
        negativePrompt,
        imageUrl: keyframeUrl || undefined,
        model,
        durationSeconds: duration,
        aspectRatio,
        loop,
        motion: motionIntensity,
      };

      let remoteVideoUrl: string | null = null;

      try {
        const response = await fetch('/api/v1/media/videos/generate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (response.ok || response.status === 202) {
          const json = (await response.json()) as any;
          if (json?.data?.videoUrl) {
            remoteVideoUrl = json.data.videoUrl;
          }
        }
      } catch {
        // Fall back to client simulation
      }

      // Progress animation across stages
      const stages = [
        { progress: 25, stage: 'Synthesizing scene semantics and 3D depth maps...' },
        { progress: 50, stage: 'Calculating virtual camera trajectory & optic flow...' },
        { progress: 75, stage: 'Simulating fluid physics & volumetric motion...' },
        { progress: 95, stage: 'Encoding MP4 stream with H.264 high profile...' },
      ];

      for (const st of stages) {
        await new Promise((resolve) => setTimeout(resolve, 400));
        set({ generationProgress: st.progress, generationStage: st.stage });
      }

      await new Promise((resolve) => setTimeout(resolve, 300));

      // Construct generated video clip
      const newVideoClip: GeneratedVideoClip = {
        id: `vid-${Date.now()}`,
        videoUrl:
          remoteVideoUrl ||
          'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
        thumbnailUrl:
          keyframeUrl ||
          'https://images.unsplash.com/photo-1508739773434-c26b3d09e071?w=800&auto=format&fit=crop&q=80',
        prompt,
        negativePrompt,
        keyframeUrl,
        model,
        duration,
        aspectRatio,
        cameraDirection,
        motionIntensity,
        loop,
        createdAt: new Date().toISOString(),
      };

      set((state) => ({
        isGenerating: false,
        generationProgress: 100,
        generationStage: 'Cinematic video generated successfully!',
        generatedVideos: [newVideoClip, ...state.generatedVideos],
        activeVideo: newVideoClip,
      }));

      return true;
    } catch (err: any) {
      // Rollback credits on error
      walletStore.rollbackDeduct(requiredCredits);
      set({
        isGenerating: false,
        generationProgress: 0,
        errorMessage: err?.message || 'Video generation failed. Credits have been refunded.',
      });
      return false;
    }
  },

  deleteVideo: (id: string) => {
    set((state) => {
      const filtered = state.generatedVideos.filter((v) => v.id !== id);
      return {
        generatedVideos: filtered,
        activeVideo: state.activeVideo?.id === id ? filtered[0] || null : state.activeVideo,
      };
    });
  },

  resetStudio: () => {
    set({
      prompt:
        'A hyper-realistic cinematic aerial shot gliding through ancient neon-lit cyber temple in rain, cinematic lighting, volumetric mist, photorealistic 8k',
      negativePrompt: 'blurry, low quality, static, deformed, flickering, watermark',
      keyframeUrl: null,
      model: 'luma-dream-machine',
      duration: 5,
      aspectRatio: '16:9',
      cameraDirection: 'zoom-in',
      motionIntensity: 5,
      loop: false,
      isGenerating: false,
      isEnhancingPrompt: false,
      generationProgress: 0,
      errorMessage: null,
    });
  },
}));
